import React, { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import AutoGrowTextarea from "./AutoGrowTextarea";

/**
 * "Unable to complete" — collects the required note and hands a task back to
 * whoever created it.
 *
 * Shared by the intake-worker and admin dashboards so the wording and the
 * note-is-required rule live in one place. Rendered only while a task is
 * selected, so its state resets between uses.
 *
 * onSubmit(note) should perform the request and may throw; this keeps the modal
 * open and shows the error so the worker does not lose what they typed.
 */
export default function TaskReturnModal({ task, onCancel, onSubmit }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      setError("Please say why you could not complete it.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(trimmed);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          "Failed to send the task back. Please try again."
      );
      setSaving(false);
    }
  };

  return (
    <Modal show centered onHide={saving ? undefined : onCancel}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="h5">Unable to Complete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <p className="mb-2 small text-muted">
          This sends the task back to{" "}
          <strong>{task.created_by_name || "whoever created it"}</strong> with
          your note, and takes it off your dashboard.
        </p>

        <div className="border rounded bg-light p-2 mb-3 small fst-italic">
          {task.task_text}
        </div>

        <Form.Group>
          <Form.Label className="small fw-semibold">
            Why couldn't it be completed? <span className="text-danger">*</span>
          </Form.Label>
          <AutoGrowTextarea
            rows={3}
            value={note}
            autoFocus
            disabled={saving}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Left a voicemail, no answer by end of day — needs a follow-up call tomorrow."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="warning" onClick={submit} disabled={saving}>
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <i className="bi bi-arrow-return-left me-1" />
              Send Back
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
